/**
 * InstanceContext — exposes the partner instance + parent-site metadata
 * the bootstrap stuffed into the iframe URL. Owns two independent async
 * lifecycles:
 *
 *   1. Resolve partner config (theme/feature_flags/i18n/catalog_filter)
 *      via /partners/v1/embedded/resolve. Drives the visible theme.
 *   2. Self-healing guest session — fires createGuest() whenever
 *      authState transitions to 'unauthenticated' (initial visit or
 *      post-deploy session-cleared). Retries with backoff on transient
 *      failure so a slow first byte never wedges the UI.
 *
 * Strict-mode safe: cleanup CANCELS in-flight work, but state is
 * keyed on `bootstrappedFor*` refs that track WHAT we already resolved,
 * not WHETHER an attempt fired. So a cancelled-then-remounted effect
 * picks up cleanly.
 *
 * Chat session state lives in <ChatSessionProvider>; not surfaced here.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import logger from '../utils/logger';
import i18n from '../i18n';
import { resolveLocale } from '../lang/resolveLocale';
import { askLabelFor } from '../lang/askTemplates';
import { pickLocalizedString } from '../lang/pickLocalizedString';
import {
  resolveInstance,
  type ResolvedInstanceConfig,
} from '../api/instanceApi';
import {
  applyThemeToDocument,
  postLauncherToParent,
  postLauncherSuggestionsToParent,
} from '../theme/applyTheme';
import { configureTelemetry, emit as emitTelemetry } from '../utils/telemetry';
import type { EmbedTheme } from '@wg/theme';
import { setEffectiveCampaign } from '../utils/campaign';

export interface InstanceParams {
  instanceId: string | null;
  parentOrigin: string | null;
  locale: string;
  tz: string;
  theme: string;
  // Campaign override handed down by the host page via data-wg-campaign
  // (wg-explore forwards its ?campaign= first touch). Sent to the resolve
  // endpoint, which decides whether this instance may use it — the
  // campaign_code in the returned config is already the EFFECTIVE one.
  campaign: string;
  // Host-supplied trip title via data-wg-trip-title (host localizes it).
  // Consumed by ItineraryContext: used at create, or renames a still-default
  // title once; never overrides a user-set title.
  tripTitle: string;
}

interface InstanceContextValue extends InstanceParams {
  resolved: boolean;
  error: string | null;
  config: ResolvedInstanceConfig | null;
  resolvedTheme: EmbedTheme | null;
  effectiveLocale: string;
}

const InstanceContext = createContext<InstanceContextValue | undefined>(
  undefined,
);

function readParams(): InstanceParams {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      instanceId: p.get('instance'),
      parentOrigin: p.get('origin'),
      locale: p.get('locale') || navigator.language || 'en-US',
      tz: p.get('tz') || Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      theme: p.get('theme') || '',
      campaign: p.get('campaign') || '',
      tripTitle: (p.get('trip_title') || '').trim().slice(0, 120),
    };
  } catch {
    return {
      instanceId: null,
      parentOrigin: null,
      locale: 'en-US',
      tz: '',
      theme: '',
      campaign: '',
      tripTitle: '',
    };
  }
}

// Same retry policy as ChatSessionContext — keep them in sync so the
// auth + chat layers behave identically under transient failure.
const MAX_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 8000;
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
const backoff = (attempt: number) =>
  Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS) *
  (0.5 + Math.random());

export const InstanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [params] = useState<InstanceParams>(readParams);
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<ResolvedInstanceConfig | null>(null);
  const [resolvedTheme, setResolvedTheme] = useState<EmbedTheme | null>(null);

  // Track success per identifier. NOT a "did we start" flag — those break
  // when strict-mode cancels mid-flight and refs persist.
  const themeResolvedFor = useRef<string | null>(null);
  const guestCreatedForState = useRef<string | null>(null);

  const { authState, createGuest } = useAuth();

  // ── Resolve partner instance config + apply theme ─────────────────
  useEffect(() => {
    const key = `${params.instanceId ?? 'none'}|${params.theme}|${params.parentOrigin ?? 'none'}`;
    if (themeResolvedFor.current === key) return;

    let cancelled = false;

    const applyFallback = () => {
      const fallback = params.theme ? { preset: params.theme } : {};
      const theme = applyThemeToDocument(fallback);
      if (cancelled) return;
      setResolvedTheme(theme);
      setResolved(true);
      postLauncherToParent(theme, params.parentOrigin || '*');
      themeResolvedFor.current = key;
    };

    if (!params.instanceId) {
      applyFallback();
      return;
    }

    (async () => {
      let lastError: unknown = null;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          const cfg = await resolveInstance(
            params.instanceId!,
            params.parentOrigin,
            params.campaign || null,
          );
          if (cancelled) return;
          setConfig(cfg);
          setEffectiveCampaign(cfg.campaign_code);
          // Honor `?theme=<preset>` URL override (POC demo) — show bare
          // preset without partner overrides; otherwise use the resolved
          // theme.
          const themeInput = params.theme
            ? { preset: params.theme }
            : cfg.theme;
          // Localize launcher.label from the strings table when a row
          // covers the visitor's locale. Falls through to whatever the
          // theme JSONB has (legacy single-string path) when no row
          // matches. Uses the same effectiveLocale derivation below.
          // Same precedence as `effectiveLocale` below — force_locale (rare
          // partner lock) wins; otherwise device language. default_locale
          // intentionally dropped from precedence per Phase 6.
          const localeForLauncher =
            (typeof cfg.i18n?.force_locale === 'string' &&
              cfg.i18n.force_locale.trim()) ||
            params.locale ||
            'en-US';
          const localizedLabel = pickLocalizedString<string>(
            cfg.strings,
            'launcher_label',
            localeForLauncher,
          );
          // Launcher nametag (theme.launcher.showName): the name rendered is
          // the instance persona.display_name — the SAME source the open
          // panel's header pill uses — injected here so the bootstrap never
          // needs its own identity lookup. No display_name → no nametag.
          const personaDisplayName = (
            cfg.persona as { display_name?: unknown } | undefined
          )?.display_name;
          const agentName =
            typeof personaDisplayName === 'string' && personaDisplayName.trim()
              ? personaDisplayName.trim()
              : undefined;
          // Compact pill label: only when the instance opted into compact
          // (theme.launcher.compact present). Per-locale `compact_label`
          // strings row wins; otherwise the built-in localized "Ask {name}"
          // template with the SAME display name the panel header uses.
          // Injected as compact.askLabel — partner-authored keys are merged
          // under it, never replaced.
          const compactCfg = (
            themeInput as {
              launcher?: { compact?: Record<string, unknown> };
            } | null
          )?.launcher?.compact;
          let compactExtras: Record<string, unknown> | null = null;
          if (compactCfg && typeof compactCfg === 'object') {
            const localizedCompactLabel = pickLocalizedString<string>(
              cfg.strings,
              'compact_label',
              localeForLauncher,
            );
            const askLabel =
              localizedCompactLabel ||
              (agentName
                ? askLabelFor(localeForLauncher, agentName)
                : undefined);
            compactExtras = {
              ...compactCfg,
              ...(askLabel ? { askLabel } : {}),
            };
          }
          const launcherExtras: Record<string, unknown> = {
            ...(localizedLabel ? { label: localizedLabel } : {}),
            ...(agentName ? { agentName } : {}),
            ...(compactExtras ? { compact: compactExtras } : {}),
          };
          const themeWithLocalizedLabel = Object.keys(launcherExtras).length
            ? {
                ...(themeInput as Record<string, unknown>),
                launcher: {
                  ...((themeInput as { launcher?: Record<string, unknown> })
                    ?.launcher || {}),
                  ...launcherExtras,
                },
              }
            : themeInput;
          const theme = applyThemeToDocument(themeWithLocalizedLabel);
          setResolvedTheme(theme);
          setResolved(true);
          postLauncherToParent(theme, params.parentOrigin || '*');
          // Launcher suggestion pills — admin sets per-instance via
          // persona.collapsed.quick_questions, falls back to the same
          // suggestion list ChatView uses in the welcome screen so an
          // instance never has badges that contradict its in-chat copy.
          // Locale precedence matches launcher.label (strings → legacy
          // i18n.suggestions). Capped to 3 by both ends.
          const localizedSuggestions = pickLocalizedString<string[]>(
            cfg.strings,
            'suggestions',
            localeForLauncher,
          );
          const collapsedQuestions = (
            cfg.persona as
              | { collapsed?: { quick_questions?: unknown } }
              | undefined
          )?.collapsed?.quick_questions;
          const launcherSuggestions = Array.isArray(collapsedQuestions)
            ? (collapsedQuestions as unknown[]).filter(
                (s): s is string => typeof s === 'string' && !!s.trim(),
              )
            : Array.isArray(localizedSuggestions) &&
                localizedSuggestions.length > 0
              ? localizedSuggestions
              : Array.isArray(cfg.i18n?.suggestions)
                ? (cfg.i18n!.suggestions as string[])
                : [];
          postLauncherSuggestionsToParent(
            launcherSuggestions,
            params.parentOrigin || '*',
          );
          configureTelemetry({
            instanceId: cfg.instance_id,
            partnerId: cfg.partner_id,
            parentOrigin: params.parentOrigin,
            instanceKey: cfg.instance_id,
          });
          emitTelemetry('widget_open', {
            props: {
              parent_origin: params.parentOrigin,
              locale: params.locale,
              tz: params.tz,
            },
          });
          themeResolvedFor.current = key;
          return;
        } catch (err) {
          if (cancelled) return;
          lastError = err;
          // 4xx (e.g. 403 origin-not-allowed) is permanent — don't keep
          // retrying. Drop straight to fallback theme.
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          if (typeof status === 'number' && status >= 400 && status < 500) {
            logger.warn(
              `[embed] resolve failed permanently (${status}) — using defaults`,
              err,
            );
            applyFallback();
            return;
          }
        }
        if (attempt < MAX_ATTEMPTS - 1) {
          logger.warn(
            `[embed] resolve attempt ${attempt + 1} failed, retrying`,
            lastError,
          );
          await sleep(backoff(attempt));
        }
      }
      // Exhausted retries on transient errors — paint the fallback theme
      // so the widget at least RENDERS rather than staying transparent.
      logger.error(
        '[embed] resolve exhausted retries — using defaults',
        lastError,
      );
      applyFallback();
    })();

    return () => {
      cancelled = true;
    };
  }, [
    params.instanceId,
    params.parentOrigin,
    params.locale,
    params.tz,
    params.theme,
  ]);

  // ── Self-healing guest session ────────────────────────────────────
  // AuthProvider hydrates from storage on mount:
  //   null → 'guest'/'authenticated' (token in storage)
  //   null → 'unauthenticated' (no token)
  // We fire createGuest() on every transition to 'unauthenticated' —
  // covers initial visit AND post-deploy sessionCleared. Retries on
  // transient failure so a slow first /auth/guest call doesn't wedge
  // the UI in 'unauthenticated' forever.
  useEffect(() => {
    if (authState !== 'unauthenticated') return;
    // Don't retry-storm if a previous attempt succeeded and authState
    // somehow flipped back without a clear reason. The 'unauthenticated'
    // → run mapping is keyed on the guestCreatedForState ref so a fresh
    // sessionCleared event (which moves authState THROUGH 'unauthenticated'
    // again) DOES trigger createGuest, while spurious re-renders don't.
    if (guestCreatedForState.current === 'unauthenticated') return;
    guestCreatedForState.current = 'unauthenticated';

    let cancelled = false;
    setError(null);

    (async () => {
      let lastError: unknown = null;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          await createGuest({
            instanceId: params.instanceId ?? undefined,
            parentOrigin: params.parentOrigin ?? undefined,
            source: 'embed',
            extra: {
              locale: params.locale,
              tz: params.tz,
              theme: params.theme,
            },
          });
          if (cancelled) return;
          // Success — AuthProvider's onAuthEvent listener will flip
          // authState to 'guest', releasing this ref to allow a future
          // sessionCleared → re-fire.
          return;
        } catch (err) {
          if (cancelled) return;
          lastError = err;
        }
        if (attempt < MAX_ATTEMPTS - 1) {
          logger.warn(
            `[embed] createGuest attempt ${attempt + 1} failed, retrying`,
            lastError,
          );
          await sleep(backoff(attempt));
        }
      }
      if (cancelled) return;
      logger.error('[embed] createGuest exhausted retries', lastError);
      setError('Failed to start session');
      // Allow a future authState change (or full reload) to re-trigger.
      guestCreatedForState.current = null;
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authState,
    createGuest,
    params.instanceId,
    params.parentOrigin,
    params.locale,
    params.tz,
    params.theme,
  ]);

  // Reset the guest-create gate when authState moves OFF 'unauthenticated'
  // — so the next sessionCleared (e.g., after a deploy) actually fires
  // createGuest again instead of being blocked by the ref.
  useEffect(() => {
    if (authState !== 'unauthenticated') {
      guestCreatedForState.current = null;
    }
  }, [authState]);

  const effectiveLocale = useMemo(() => {
    // Phase 6 of embed-pilot-readiness: device language wins by default.
    // Only `force_locale` (explicit partner lock — rare) overrides the
    // visitor's device language. `default_locale` is intentionally NOT in
    // this precedence chain — it's the deprecated legacy field that used
    // to override device language unconditionally; that direction was
    // wrong (a Japanese visitor on a partner page should see Japanese
    // even if the partner forgot to flip a setting).
    if (
      typeof config?.i18n?.force_locale === 'string' &&
      config.i18n.force_locale.trim()
    ) {
      return config.i18n.force_locale;
    }
    return params.locale || 'en-US';
  }, [config, params.locale]);

  // Push the resolved locale into i18next so useTranslation calls switch
  // languages. We canonicalize through resolveLocale first so that:
  //   - 'zh-CN' / 'zh-SG' → 'zh-Hans'
  //   - 'zh-TW' / 'zh-HK' → 'zh-Hant'
  //   - 'no' → 'nb'
  //   - 'en-US' / 'ja-JP' → 'en' / 'ja'
  // and i18next then lazy-loads the matching JSON via languageBackend.
  // Missing locales fall back to 'en' silently (returnEmptyString: false).
  useEffect(() => {
    if (!effectiveLocale) return;
    const canonical = resolveLocale(effectiveLocale);
    if (i18n.language === canonical) return;
    i18n.changeLanguage(canonical).catch(err => {
      logger.warn('[instance] i18n.changeLanguage failed', err);
    });
  }, [effectiveLocale]);

  const value = useMemo<InstanceContextValue>(
    () => ({
      ...params,
      resolved,
      error,
      config,
      resolvedTheme,
      effectiveLocale,
    }),
    [params, resolved, error, config, resolvedTheme, effectiveLocale],
  );

  return (
    <InstanceContext.Provider value={value}>
      {children}
    </InstanceContext.Provider>
  );
};

export function useInstance(): InstanceContextValue {
  const ctx = useContext(InstanceContext);
  if (!ctx) throw new Error('useInstance must be used within InstanceProvider');
  return ctx;
}
